import { supabase } from '../lib/supabase';

export const advisorService = {
  async getAdvisorStats(advisorId) {
    try {
      // Use advisor_metrics table instead of advisor_stats
      const { data, error } = await supabase?.from('advisor_metrics')?.select('*')?.eq('advisor_id', advisorId)?.eq('month', new Date()?.toISOString()?.slice(0, 7) + '-01')?.maybeSingle();
      
      if (error) throw error;
      
      // If no record exists for current month, create one
      if (!data) {
        const currentMonth = new Date()?.toISOString()?.slice(0, 7) + '-01';
        const { data: newData, error: insertError } = await supabase?.from('advisor_metrics')?.insert({ 
          advisor_id: advisorId,
          month: currentMonth,
          total_earnings: 0,
          active_referrals: 0,
          points_accumulated: 0,
          leads_converted: 0
        })?.select()?.maybeSingle();
        
        if (insertError) {
          console.error('Error creating advisor metrics:', insertError);
          // Return default values if creation fails
          return {
            totalReferrals: 0,
            activeReferrals: 0,
            totalLeads: 0,
            activeLeads: 0,
            monthlyEarnings: 0,
            totalEarnings: 0
          };
        }
        
        return {
          totalReferrals: newData?.active_referrals || 0,
          activeReferrals: newData?.active_referrals || 0,
          totalLeads: newData?.leads_converted || 0,
          activeLeads: newData?.leads_converted || 0,
          monthlyEarnings: parseFloat(newData?.total_earnings || 0),
          totalEarnings: parseFloat(newData?.total_earnings || 0),
          updatedAt: newData?.updated_at
        };
      }
      
      return {
        totalReferrals: data?.active_referrals || 0,
        activeReferrals: data?.active_referrals || 0,
        totalLeads: data?.leads_converted || 0,
        activeLeads: data?.leads_converted || 0,
        monthlyEarnings: parseFloat(data?.total_earnings || 0),
        totalEarnings: parseFloat(data?.total_earnings || 0),
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching advisor stats:', error);
      return {
        totalReferrals: 0,
        activeReferrals: 0,
        totalLeads: 0,
        activeLeads: 0,
        monthlyEarnings: 0,
        totalEarnings: 0
      };
    }
  },

  async getLeads(advisorId) {
    try {
      const { data, error } = await supabase?.from('leads')?.select('*')?.eq('advisor_id', advisorId)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(lead => ({
        id: lead?.id,
        fullName: lead?.full_name,
        email: lead?.email,
        phone: lead?.phone,
        source: lead?.source,
        projectInterest: lead?.project_interest,
        budget: parseFloat(lead?.budget || 0),
        status: lead?.status,
        interestLevel: lead?.interest_level,
        lastActivity: lead?.last_activity,
        nextFollowup: lead?.next_followup,
        notes: lead?.notes,
        createdAt: lead?.created_at,
        updatedAt: lead?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching leads:', error);
      return [];
    }
  },

  async createLead(advisorId, leadData) {
    try {
      const { data, error } = await supabase?.from('leads')?.insert({
          advisor_id: advisorId,
          full_name: leadData?.fullName,
          email: leadData?.email,
          phone: leadData?.phone || null,
          source: leadData?.source || null,
          project_interest: leadData?.projectInterest || null,
          budget: leadData?.budget || null,
          status: leadData?.status || 'new',
          interest_level: leadData?.interestLevel || 'warm',
          notes: leadData?.notes || null
        })?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateLead(leadId, updates) {
    try {
      const dbUpdates = {};
      if (updates?.fullName !== undefined) dbUpdates.full_name = updates?.fullName;
      if (updates?.email !== undefined) dbUpdates.email = updates?.email;
      if (updates?.phone !== undefined) dbUpdates.phone = updates?.phone;
      if (updates?.source !== undefined) dbUpdates.source = updates?.source;
      if (updates?.projectInterest !== undefined) dbUpdates.project_interest = updates?.projectInterest;
      if (updates?.budget !== undefined) dbUpdates.budget = updates?.budget;
      if (updates?.status !== undefined) dbUpdates.status = updates?.status;
      if (updates?.interestLevel !== undefined) dbUpdates.interest_level = updates?.interestLevel;
      if (updates?.lastActivity !== undefined) dbUpdates.last_activity = updates?.lastActivity;
      if (updates?.nextFollowup !== undefined) dbUpdates.next_followup = updates?.nextFollowup;
      if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes;

      const { data, error } = await supabase?.from('leads')?.update(dbUpdates)?.eq('id', leadId)?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteLead(leadId) {
    try {
      const { error } = await supabase?.from('leads')?.delete()?.eq('id', leadId);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async getReferrals(advisorId) {
    try {
      const { data, error } = await supabase?.from('referrals')?.select(`
          *,
          referred_user:user_profiles!referrals_referred_user_id_fkey(
            id,
            full_name,
            email,
            created_at
          )
        `)?.eq('referrer_id', advisorId)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(referral => ({
        id: referral?.id,
        referralCode: referral?.referral_code,
        status: referral?.status,
        commissionEarned: parseFloat(referral?.commission_earned || 0),
        createdAt: referral?.created_at,
        referredUser: referral?.referred_user ? {
          id: referral?.referred_user?.id,
          fullName: referral?.referred_user?.full_name,
          email: referral?.referred_user?.email,
          createdAt: referral?.referred_user?.created_at
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching referrals:', error);
      return [];
    }
  },

  // Build recursive referral tree
  async buildReferralTree(userId, maxDepth = 5) {
    try {
      const buildTree = async (currentUserId, currentLevel = 1) => {
        if (currentLevel > maxDepth) return [];
        
        // Get direct referrals from referral_network
        const { data, error } = await supabase
          ?.from('referral_network')
          ?.select(`
            *,
            referred:user_profiles!referral_network_referred_id_fkey(
              id,
              full_name,
              email,
              avatar_url,
              phone,
              created_at,
              is_active
            )
          `)
          ?.eq('referrer_id', currentUserId)
          ?.order('created_at', { ascending: false });
        
        if (error) throw error;
        
        if (!data || data?.length === 0) return [];
        
        // Recursively build children for each referral
        const children = await Promise.all(
          data?.map(async (ref) => {
            const childReferrals = await buildTree(ref?.referred_id, currentLevel + 1);
            
            return {
              id: ref?.referred_id,
              name: ref?.referred?.full_name || 'Usuario',
              email: ref?.referred?.email || '',
              phone: ref?.referred?.phone || '',
              avatar: ref?.referred?.avatar_url || null,
              registrationDate: new Date(ref?.referred?.created_at)?.toLocaleDateString('es-PE'),
              isActive: ref?.referred?.is_active || false,
              level: ref?.level || currentLevel,
              commissionRate: parseFloat(ref?.commission_rate || 0),
              totalCommission: parseFloat(ref?.total_commission || 0),
              totalReferrals: childReferrals?.length || 0,
              children: childReferrals
            };
          })
        );
        
        return children;
      };
      
      return await buildTree(userId);
    } catch (error) {
      console.error('Error building referral tree:', error);
      return [];
    }
  },

  // Get commission breakdown by level
  async getCommissionsByLevel(advisorId) {
    try {
      const { data, error } = await supabase?.from('referral_network')?.select(`
          level,
          commission_rate,
          total_commission,
          referred:user_profiles!referral_network_referred_id_fkey(id, full_name)
        `)?.eq('referrer_id', advisorId)?.order('level', { ascending: true });
      
      if (error) throw error;
      
      // Group by level and calculate totals
      const levelMap = {};
      data?.forEach(item => {
        let level = item?.level || 1;
        if (!levelMap?.[level]) {
          levelMap[level] = {
            level,
            amount: 0,
            referrals: 0,
            percentage: parseFloat(item?.commission_rate || 0)
          };
        }
        levelMap[level].amount += parseFloat(item?.total_commission || 0);
        levelMap[level].referrals += 1;
      });
      
      // Ensure all 5 levels are represented with production commission rates
      const productionRates = {
        1: 10.00,  // Direct sale
        2: 2.50,   // 1st generation
        3: 1.50,   // 2nd generation
        4: 0.60,   // 3rd generation
        5: 0.40    // 4th generation
      };
      
      for (let level = 1; level <= 5; level++) {
        if (!levelMap?.[level]) {
          levelMap[level] = {
            level,
            amount: 0,
            referrals: 0,
            percentage: productionRates?.[level]
          };
        } else {
          // Update to production rates
          levelMap[level].percentage = productionRates?.[level];
        }
      }
      
      return Object.values(levelMap)?.sort((a, b) => a?.level - b?.level);
    } catch (error) {
      console.error('Error fetching commissions by level:', error);
      return [];
    }
  },

  // Get earnings history (monthly breakdown)
  async getEarningsHistory(advisorId, months = 6) {
    try {
      const { data, error } = await supabase?.from('advisor_metrics')?.select('month, total_earnings')?.eq('advisor_id', advisorId)?.order('month', { ascending: false })?.limit(months);
      
      if (error) throw error;
      
      return data?.map(item => ({
        month: item?.month,
        earnings: parseFloat(item?.total_earnings || 0)
      }))?.reverse() || [];
    } catch (error) {
      console.error('Error fetching earnings history:', error);
      return [];
    }
  },

  // Get activity feed (recent actions)
  async getActivityFeed(advisorId, limit = 10) {
    try {
      // Combine leads and referrals for activity feed
      const [leadsResult, referralsResult] = await Promise.all([
        supabase?.from('leads')?.select('id, full_name, status, created_at, updated_at')?.eq('advisor_id', advisorId)?.order('updated_at', { ascending: false })?.limit(limit),
        supabase?.from('referrals')?.select(`
            id,
            status,
            created_at,
            referred_user:user_profiles!referrals_referred_user_id_fkey(full_name)
          `)?.eq('referrer_id', advisorId)?.order('created_at', { ascending: false })?.limit(limit)
      ]);
      
      const activities = [];
      
      // Add lead activities
      leadsResult?.data?.forEach(lead => {
        activities?.push({
          id: `lead-${lead?.id}`,
          type: 'lead',
          title: `Lead: ${lead?.full_name}`,
          description: `Estado: ${lead?.status}`,
          timestamp: lead?.updated_at || lead?.created_at,
          icon: 'user'
        });
      });
      
      // Add referral activities
      referralsResult?.data?.forEach(ref => {
        activities?.push({
          id: `referral-${ref?.id}`,
          type: 'referral',
          title: `Nuevo referido: ${ref?.referred_user?.full_name}`,
          description: `Estado: ${ref?.status}`,
          timestamp: ref?.created_at,
          icon: 'users'
        });
      });
      
      // Sort by timestamp and limit
      return activities?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.slice(0, limit);
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      return [];
    }
  },

  // Get available rewards
  async getAvailableRewards() {
    try {
      const { data, error } = await supabase?.from('rewards')?.select('*')?.eq('is_active', true)?.order('points_required', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(reward => ({
        id: reward?.id,
        name: reward?.name,
        description: reward?.description,
        pointsRequired: reward?.points_required,
        rewardType: reward?.reward_type,
        rewardValue: parseFloat(reward?.reward_value || 0),
        imageUrl: reward?.image_url
      })) || [];
    } catch (error) {
      console.error('Error fetching rewards:', error);
      return [];
    }
  },

  // Claim a reward
  async claimReward(advisorId, rewardId) {
    try {
      const { data, error } = await supabase?.from('advisor_rewards')?.insert({
          advisor_id: advisorId,
          reward_id: rewardId,
          status: 'pending'
        })?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};